import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Warning,
  ContentCopy,
  LinkOff,
  MergeType,
  CheckCircle,
  Block,
} from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';

// Mock data for Data Quality Dashboard

const mockInactiveItems = [
  { id: 1, itemNumber: 'FLR-003', description: 'Wholemeal Flour (Discontinued)', status: 'Inactive', linkedRecipes: ['Wholemeal Bread', 'Brown Loaf'], lastUsed: '2025-09-15' },
  { id: 2, itemNumber: 'CHZ-002', description: 'Cheddar Mild (Delisted)', status: 'Inactive', linkedRecipes: ['Cheese Toastie', 'Ploughman\'s Sandwich'], lastUsed: '2025-11-01' },
  { id: 3, itemNumber: 'HRB-004', description: 'Dried Oregano (Old Supplier)', status: 'Inactive', linkedRecipes: ['Pizza Margherita', 'House Tomato Sauce'], lastUsed: '2026-01-10' },
];

const mockDuplicates = [
  { id: 1, items: [{ itemNumber: 'TOM-001', description: 'Chopped Tomatoes (Tinned)' }, { itemNumber: 'TOM-003', description: 'Chopped Tomatoes (Canned)' }], matchType: 'Description', confidence: 0.92 },
  { id: 2, items: [{ itemNumber: 'OLV-001', description: 'Extra Virgin Olive Oil' }, { itemNumber: 'OLV-002', description: 'EVOO Premium' }], matchType: 'Supplier SKU', confidence: 0.85 },
  { id: 3, items: [{ itemNumber: 'BTR-001', description: 'Unsalted Butter' }, { itemNumber: 'BTR-003', description: 'Butter Unsalted Block' }], matchType: 'GTIN', confidence: 0.98 },
];

const mockOrphans = [
  { id: 1, itemNumber: 'SPC-009', description: 'Saffron Threads', lastOrdered: '2025-06-01', costPrice: 45.00 },
  { id: 2, itemNumber: 'NUT-012', description: 'Macadamia Nuts (Whole)', lastOrdered: '2025-08-20', costPrice: 32.00 },
  { id: 3, itemNumber: 'VEG-018', description: 'Purple Sprouting Broccoli', lastOrdered: '2026-01-15', costPrice: 8.50 },
  { id: 4, itemNumber: 'FRT-007', description: 'Passion Fruit (Case)', lastOrdered: '2025-12-01', costPrice: 22.00 },
];

const mockConsolidation = [
  { id: 1, inventorySku: 'TOM-001', omContentRecords: 3, suppliers: ['FreshCo', 'Brakes', 'Local Farm'], action: null as string | null },
  { id: 2, inventorySku: 'OLV-001', omContentRecords: 2, suppliers: ['Mediterranean Oils', 'Bulk Oils Ltd'], action: null as string | null },
];

type DqTab = 'inactive' | 'duplicates' | 'orphans' | 'consolidation';

const DataQualityDashboard = () => {
  const { t } = useTranslation('recipeV2');
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<DqTab>('inactive');
  const [page, setPage] = useState(0);

  const summaryCards: { key: DqTab, icon: React.ReactNode, label: string, subtitle: string, count: number, accentColor: string }[] = [
    { key: 'inactive', icon: <Warning />, label: t('dq.inactiveLabel', 'Inactive in Active Recipes'), subtitle: t('dq.inactiveSubtitle', 'Linked to active recipes'), count: mockInactiveItems.length, accentColor: '#ed6c02' },
    { key: 'duplicates', icon: <ContentCopy />, label: t('dq.duplicatesLabel', 'Potential Duplicates'), subtitle: t('dq.duplicatesSubtitle', 'Similar items detected'), count: mockDuplicates.length, accentColor: '#d32f2f' },
    { key: 'orphans', icon: <LinkOff />, label: t('dq.orphansLabel', 'Orphan Items'), subtitle: t('dq.orphansSubtitle', 'Not used in any recipe or order'), count: mockOrphans.length, accentColor: '#1976d2' },
    { key: 'consolidation', icon: <MergeType />, label: t('dq.consolidationLabel', 'Consolidation Candidates'), subtitle: t('dq.consolidationSubtitle', 'Multiple OM records per SKU'), count: mockConsolidation.length, accentColor: '#9c27b0' },
  ];

  const activeRowCount = {
    inactive: mockInactiveItems.length,
    duplicates: mockDuplicates.length,
    orphans: mockOrphans.length,
    consolidation: mockConsolidation.length,
  }[activeTab];

  return (
    <PageArea>
      <Stack spacing={3}
        width="100%">
        <PageTitle subTitle={t('dq.pageSubtitle', 'Identify and resolve data quality issues across recipes and stock items')}>
          {t('dq.pageTitle', 'Data Quality')}
        </PageTitle>

        {/* Summary tiles */}
        <Grid container
          spacing={2}>
          {summaryCards.map((card) => (
            <Grid item
              xs={12}
              sm={6}
              md={3}
              key={card.key}>
              <Section
                onClick={() => setActiveTab(card.key)}
                style={{
                  p: 4,
                  mb: 0,
                  cursor: 'pointer',
                  borderColor: activeTab === card.key ? card.accentColor : theme.palette.custom.gray[300],
                  borderWidth: activeTab === card.key ? 2 : 1,
                }}
              >
                <Box display="flex"
                  alignItems="center"
                  justifyContent="space-between">
                  <Box flex={1}>
                    <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                      {card.label}
                    </Box>
                    <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                      {card.count}
                    </Box>
                    <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                      {card.subtitle}
                    </Box>
                  </Box>
                  <Box sx={{ backgroundColor: `${card.accentColor}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.accentColor }}>
                    <SvgIcon fontSize="medium">{card.icon}</SvgIcon>
                  </Box>
                </Box>
              </Section>
            </Grid>
          ))}
        </Grid>

        {/* Tab content */}
        <Tabs value={activeTab}
          onChange={(_, v) => { setActiveTab(v); setPage(0); }}>
          <Tab label={t('dq.tabInactive', 'Inactive Items')}
            value="inactive" />
          <Tab label={t('dq.tabDuplicates', 'Duplicates')}
            value="duplicates" />
          <Tab label={t('dq.tabOrphans', 'Orphan Items')}
            value="orphans" />
          <Tab label={t('dq.tabConsolidation', 'Consolidation')}
            value="consolidation" />
        </Tabs>

        {/* Inactive Items */}
        {activeTab === 'inactive' && (
          <Section>
            <TableHeader>
              {t('dq.inactiveTableTitle', 'Inactive items linked to active recipes')}
              <Pill text={`${activeRowCount} ${t('dq.inactiveItemsLabel', 'items')}`} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item Number</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Linked Recipes</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockInactiveItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography fontFamily="monospace"
                          variant="textSM">{item.itemNumber}</Typography>
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>
                        <Chip label={item.status}
                          size="small"
                          color="error" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}
                          flexWrap="wrap">
                          {item.linkedRecipes.map((r) => (
                            <Chip key={r}
                              label={r}
                              size="small"
                              variant="outlined" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>{item.lastUsed}</TableCell>
                      <TableCell>
                        <Button size="md"
                          variant="secondary">Replace</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination useNoDataMessage={false}
              page={page}
              setPage={setPage}
              maxPages={1}
              breakpointForChangeDisplay={120000} />
          </Section>
        )}

        {/* Duplicates */}
        {activeTab === 'duplicates' && (
          <Section>
            <TableHeader>
              {t('dq.duplicatesTableTitle', 'Similar items that may be duplicates')}
              <Pill text={`${activeRowCount} ${t('dq.duplicatesItemsLabel', 'matches')}`} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Items</TableCell>
                    <TableCell>Match Type</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockDuplicates.map((dup) => (
                    <TableRow key={dup.id}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {dup.items.map((item) => (
                            <Typography key={item.itemNumber}
                              variant="textSM">
                              <Typography component="span"
                                fontFamily="monospace"
                                variant="textXS">{item.itemNumber}</Typography>
                              {' '}{item.description}
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip label={dup.matchType}
                          size="small"
                          variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography color={dup.confidence > 0.9 ? 'error.main' : 'warning.main'}
                          fontWeight={600}>
                          {(dup.confidence * 100).toFixed(0)}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}>
                          <Button size="md"
                            variant="primary"
                            startIcon={<MergeType />}>Merge</Button>
                          <Button size="md"
                            variant="secondary"
                            startIcon={<Block />}>Ignore</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination useNoDataMessage={false}
              page={page}
              setPage={setPage}
              maxPages={1}
              breakpointForChangeDisplay={120000} />
          </Section>
        )}

        {/* Orphans */}
        {activeTab === 'orphans' && (
          <Section>
            <TableHeader>
              {t('dq.orphansTableTitle', 'Items not used in any recipe or recent order')}
              <Pill text={`${activeRowCount} ${t('dq.orphansItemsLabel', 'items')}`} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item Number</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Cost Price</TableCell>
                    <TableCell>Last Ordered</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockOrphans.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography fontFamily="monospace"
                          variant="textSM">{item.itemNumber}</Typography>
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell align="right">£{item.costPrice.toFixed(2)}</TableCell>
                      <TableCell>{item.lastOrdered}</TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}>
                          <Button size="md"
                            variant="secondary"
                            startIcon={<Block />}>Deactivate</Button>
                          <Button size="md"
                            variant="secondary">Flag for Review</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination useNoDataMessage={false}
              page={page}
              setPage={setPage}
              maxPages={1}
              breakpointForChangeDisplay={120000} />
          </Section>
        )}

        {/* Consolidation */}
        {activeTab === 'consolidation' && (
          <Section>
            <TableHeader>
              {t('dq.consolidationTableTitle', 'Multiple OM Content records mapping to same InventorySKU')}
              <Pill text={`${activeRowCount} ${t('dq.consolidationItemsLabel', 'candidates')}`} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Inventory SKU</TableCell>
                    <TableCell align="center">OM Content Records</TableCell>
                    <TableCell>Suppliers</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockConsolidation.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography fontFamily="monospace"
                          variant="textSM">{item.inventorySku}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={item.omContentRecords}
                          size="small"
                          color={item.omContentRecords > 2 ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}
                          flexWrap="wrap">
                          {item.suppliers.map((s) => (
                            <Chip key={s}
                              label={s}
                              size="small"
                              variant="outlined" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row"
                          spacing={0.5}>
                          <Button size="md"
                            variant="primary"
                            startIcon={<MergeType />}>Consolidate</Button>
                          <Button size="md"
                            variant="secondary"
                            startIcon={<CheckCircle />}>OK</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination useNoDataMessage={false}
              page={page}
              setPage={setPage}
              maxPages={1}
              breakpointForChangeDisplay={120000} />
          </Section>
        )}
      </Stack>
    </PageArea>
  );
};

export default DataQualityDashboard;
